import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';

import {
  getVisibleViewport,
  isTypedFocus,
  markTypedFocus,
  useVisualViewport,
} from './use-visual-viewport';

/** Bubble this from a focused host (e.g. Payrails iframe slot) to scroll it into view. */
export const KEEP_VISIBLE_EVENT = 'mf:keep-visible';

export function requestKeepVisible(target: HTMLElement) {
  markTypedFocus(true);
  target.dispatchEvent(new CustomEvent(KEEP_VISIBLE_EVENT, { bubbles: true }));
}

function resolveAnchor(field: HTMLElement): HTMLElement {
  return field.closest('[data-keyboard-anchor]') ?? field;
}

function focusedFieldIn(container: HTMLElement): HTMLElement | null {
  const active = document.activeElement;
  if (!isTypedFocus(active) || !container.contains(active)) return null;
  return resolveAnchor(active);
}

/**
 * Scroll `field` so it sits inside the visible visual viewport (above the
 * keyboard) and below `topClearance` inside `container`.
 *
 * Instant `scrollTop` only — never `scrollIntoView`:
 * - Android Chrome cancels `behavior: 'smooth'` during Gboard animation
 * - `scrollIntoView` often moves the document instead of the inner scroller
 * - iOS + Instagram then pan the visual viewport on top, double-shifting the field
 */
export function alignFieldToVisible(
  container: HTMLElement,
  field: HTMLElement,
  topClearance = 0,
) {
  // getBoundingClientRect is visual-viewport-relative. Never add offsetTop —
  // mixing layout pan into visual coords puts the field in the wrong band
  // (ZIP off-screen on focus, then jumping to mid-screen on the first keystroke).
  const view = getVisibleViewport();
  const fieldRect = resolveAnchor(field).getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  const visualHeight = view.height || window.innerHeight;
  const visibleTop = Math.max(containerRect.top, 0) + topClearance;
  const visibleBottom = Math.min(containerRect.bottom, visualHeight) - 16;

  if (visibleBottom - visibleTop < 48) return;

  let delta = 0;
  if (fieldRect.bottom > visibleBottom) delta = fieldRect.bottom - visibleBottom;
  else if (fieldRect.top < visibleTop) delta = fieldRect.top - visibleTop;
  if (Math.abs(delta) <= 1) return;

  const maxScroll = Math.max(0, container.scrollHeight - container.clientHeight);
  container.scrollTop = Math.max(0, Math.min(maxScroll, container.scrollTop + delta));
}

const ALIGN_RETRY_MS = [0, 120, 300, 500];

function scheduleAlign(
  container: HTMLElement,
  field: HTMLElement,
  topClearance: number,
): () => void {
  const run = () => {
    if (!container.isConnected || !field.isConnected) return;
    alignFieldToVisible(container, field, topClearance);
  };
  const raf = requestAnimationFrame(run);
  const timers = ALIGN_RETRY_MS.map((ms) => window.setTimeout(run, ms));
  return () => {
    cancelAnimationFrame(raf);
    timers.forEach(clearTimeout);
  };
}

/**
 * Keep the focused input visible while the Android/iOS keyboard animates.
 * Re-runs on visualViewport resize — a single onFocus is too early on Gboard
 * and iOS Safari's keyboard slide. Do not listen to visualViewport `scroll`:
 * that is iOS pan, and pairing it with inner scrollTop causes jitter.
 */
export function useKeepFocusedVisible(
  containerRef: RefObject<HTMLElement | null>,
  topClearance = 0,
  enabled = true,
) {
  const { height, offsetTop, keyboardOpen, inset } = useVisualViewport(enabled);
  const [spacerPx, setSpacerPx] = useState(0);
  const lastAnchorRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const container = containerRef.current;
    if (!container) return;

    let cancel: (() => void) | undefined;

    const run = (field?: HTMLElement | null) => {
      cancel?.();
      const next = field ? resolveAnchor(field) : focusedFieldIn(container);
      const target =
        next && container.contains(next)
          ? next
          : lastAnchorRef.current && container.contains(lastAnchorRef.current)
            ? lastAnchorRef.current
            : null;
      if (!target) return;
      lastAnchorRef.current = target;
      cancel = scheduleAlign(container, target, topClearance);
    };

    const onFocusIn = () => run();
    const onKeepVisible = (event: Event) => {
      const node = event.target;
      if (node instanceof HTMLElement && container.contains(node)) run(node);
    };

    container.addEventListener('focusin', onFocusIn);
    container.addEventListener(KEEP_VISIBLE_EVENT, onKeepVisible);
    window.visualViewport?.addEventListener('resize', onFocusIn);
    // Instagram Android WebView often fires window.resize without visualViewport.
    window.addEventListener('resize', onFocusIn);
    return () => {
      cancel?.();
      container.removeEventListener('focusin', onFocusIn);
      container.removeEventListener(KEEP_VISIBLE_EVENT, onKeepVisible);
      window.visualViewport?.removeEventListener('resize', onFocusIn);
      window.removeEventListener('resize', onFocusIn);
    };
  }, [containerRef, topClearance, enabled]);

  useLayoutEffect(() => {
    if (!enabled) {
      lastAnchorRef.current = null;
      setSpacerPx(0);
      return;
    }
    if (!keyboardOpen) {
      setSpacerPx(0);
      return;
    }
    const container = containerRef.current;
    if (!container) return;
    const view = getVisibleViewport();
    const visualHeight = view.height || window.innerHeight;
    const bleed = Math.max(0, container.getBoundingClientRect().bottom - visualHeight);
    setSpacerPx(bleed > 8 ? Math.ceil(bleed + 24) : 24);
  }, [containerRef, height, offsetTop, keyboardOpen, inset, enabled]);

  useLayoutEffect(() => {
    if (!enabled || !keyboardOpen) return;
    const container = containerRef.current;
    if (!container) return;
    const field =
      focusedFieldIn(container) ??
      (lastAnchorRef.current && container.contains(lastAnchorRef.current)
        ? lastAnchorRef.current
        : null);
    if (!field) return;
    // Pin/height is committed in this same frame; retry while the keyboard
    // finishes sliding (a single align is still too early on iOS).
    return scheduleAlign(container, field, topClearance);
  }, [containerRef, spacerPx, height, offsetTop, keyboardOpen, topClearance, enabled]);

  const keyboardWasOpen = useRef(false);
  useEffect(() => {
    if (!enabled) {
      keyboardWasOpen.current = false;
      return;
    }
    if (keyboardOpen) {
      keyboardWasOpen.current = true;
      return;
    }
    if (!keyboardWasOpen.current) return;
    keyboardWasOpen.current = false;
    // Instagram iOS leaves the layout viewport offset after dismiss; reset
    // document scroll only — never transform, which desyncs hit targets.
    window.scrollTo(0, 0);
    const container = containerRef.current;
    if (container) container.scrollTop = 0;
  }, [enabled, keyboardOpen]);

  return { spacerPx: enabled ? spacerPx : 0 };
}
